/* Meridian — theme behaviour
   Overlays (menu, search, cart), product form, quantity, filters, grid hover.
   No libraries. */

(() => {
  'use strict';

  const scrim = () => document.querySelector('[data-scrim]');

  /* Reveal an element that was just un-hidden, so the CSS transition still runs.
     A forced reflow is synchronous; requestAnimationFrame is throttled in
     background tabs and would leave the element stuck at opacity 0. */
  function reveal(el) {
    if (!el) return;
    void el.offsetHeight;
    el.setAttribute('data-open', '');
  }

  /* ---------- Overlay controller ---------- */

  const openPanels = new Set();

  function setScrim(on) {
    const el = scrim();
    if (!el) return;
    if (on) {
      el.hidden = false;
      reveal(el);
    } else {
      el.removeAttribute('data-open');
      setTimeout(() => {
        if (openPanels.size === 0) el.hidden = true;
      }, 200);
    }
  }

  function openPanel(panel) {
    if (!panel) return;
    panel.setAttribute('data-open', '');
    panel.removeAttribute('hidden');
    openPanels.add(panel);
    document.body.style.overflow = 'hidden';
    setScrim(true);
    const focusable = panel.querySelector(
      'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    if (focusable) focusable.focus();
  }

  function closePanel(panel) {
    if (!panel) return;
    panel.removeAttribute('data-open');
    openPanels.delete(panel);
    setTimeout(() => {
      if (!panel.hasAttribute('data-open')) panel.setAttribute('hidden', '');
    }, 200);
    if (openPanels.size === 0) {
      document.body.style.overflow = '';
      setScrim(false);
    }
  }

  function closeAll() {
    [...openPanels].forEach(closePanel);
  }

  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-panel-open]');
    if (opener) {
      e.preventDefault();
      const target = document.querySelector(opener.getAttribute('data-panel-open'));
      if (target && target.hasAttribute('data-open')) {
        closePanel(target);
      } else {
        closeAll();
        openPanel(target);
      }
      return;
    }

    const closer = e.target.closest('[data-panel-close]');
    if (closer) {
      e.preventDefault();
      closePanel(closer.closest('[data-panel]'));
      return;
    }

    if (e.target.closest('[data-scrim]')) closeAll();
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') closeAll();
  });

  /* ---------- Header height ----------
     Published as --header-h so a hero that runs under an overlaid header can
     inset its own content by exactly the right amount. */

  function measureHeader() {
    const root = document.documentElement;
    const header = document.querySelector('.site-header');

    if (header) {
      root.style.setProperty('--header-h', Math.round(header.getBoundingClientRect().height) + 'px');
    }

    /* Everything above the main column still consumes viewport height, so a
       full-height hero has to subtract it or the page picks up a stray scroll.
       offsetTop is measured from the document, so it stays correct while the
       sticky header is pinned — a bounding rect would read 0 once stuck. */
    const main = document.querySelector('.site-main');
    const above = main ? Math.round(main.offsetTop) : 0;
    root.style.setProperty('--above-main', above + 'px');
  }

  measureHeader();
  window.addEventListener('resize', measureHeader);
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(measureHeader);

  /* ---------- Stuck state ----------
     Marks the header once it has actually reached the top, so it can take a
     background only when content is passing underneath it. */

  const headerSection = document.querySelector('.site-header')
    ? document.querySelector('.site-header').closest('.shopify-section')
    : null;

  function updateStuck() {
    if (!headerSection) return;
    const stuck = headerSection.getBoundingClientRect().top <= 0;
    if (stuck) headerSection.setAttribute('data-stuck', '');
    else headerSection.removeAttribute('data-stuck');
  }

  updateStuck();
  window.addEventListener('scroll', updateStuck, { passive: true });
  window.addEventListener('resize', updateStuck);

  /* ---------- Mega menu ---------- */

  const menuRoot = document.querySelector('[data-megamenu]');

  if (menuRoot) {
    const triggers = menuRoot.querySelectorAll('[data-menu-trigger]');
    let hideTimer;

    const hideAll = () => {
      menuRoot.querySelectorAll('[data-menu-panel]').forEach((p) => {
        p.setAttribute('hidden', '');
        p.removeAttribute('data-open');
      });
      triggers.forEach((t) => t.setAttribute('aria-expanded', 'false'));
      menuRoot.removeAttribute('data-menu-active');
    };

    const show = (trigger) => {
      clearTimeout(hideTimer);
      const id = trigger.getAttribute('data-menu-trigger');
      const panel = menuRoot.querySelector(`[data-menu-panel="${id}"]`);
      if (!panel) return hideAll();
      hideAll();
      panel.removeAttribute('hidden');
      reveal(panel);
      trigger.setAttribute('aria-expanded', 'true');
      menuRoot.setAttribute('data-menu-active', '');
    };

    triggers.forEach((trigger) => {
      trigger.addEventListener('mouseenter', () => show(trigger));
      trigger.addEventListener('focus', () => show(trigger));
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        const expanded = trigger.getAttribute('aria-expanded') === 'true';
        if (expanded) hideAll();
        else show(trigger);
      });
    });

    menuRoot.addEventListener('mouseleave', () => {
      hideTimer = setTimeout(hideAll, 120);
    });

    menuRoot.addEventListener('mouseenter', () => clearTimeout(hideTimer));
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') hideAll();
    });
  }

  /* ---------- Cart ---------- */

  async function refreshCart() {
    try {
      const res = await fetch(`${window.routes.cart_url}?section_id=cart-drawer`);
      const text = await res.text();
      const doc = new DOMParser().parseFromString(text, 'text/html');
      const fresh = doc.querySelector('[data-cart-contents]');
      const current = document.querySelector('[data-cart-contents]');
      if (fresh && current) current.innerHTML = fresh.innerHTML;

      const cartRes = await fetch(`${window.routes.cart_url}.js`);
      const cart = await cartRes.json();
      document.querySelectorAll('[data-cart-count]').forEach((el) => {
        el.textContent = cart.item_count;
      });
    } catch (err) {
      console.error('Cart refresh failed', err);
    }
  }

  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('[data-product-form]');
    if (!form) return;
    e.preventDefault();

    const button = form.querySelector('[data-add-button]');
    const label = button ? button.textContent : '';
    if (button) {
      button.setAttribute('aria-disabled', 'true');
      button.textContent = button.getAttribute('data-adding-text') || label;
    }

    try {
      const res = await fetch(window.routes.cart_add_url, {
        method: 'POST',
        headers: { Accept: 'application/javascript' },
        body: new FormData(form)
      });
      const data = await res.json();

      if (data.status) {
        const err = form.querySelector('[data-form-error]');
        if (err) {
          err.textContent = data.description || data.message;
          err.removeAttribute('hidden');
        }
      } else {
        await refreshCart();
        const drawer = document.querySelector('[data-panel="cart"]');
        if (drawer) {
          closeAll();
          openPanel(drawer);
        } else {
          window.location.href = window.routes.cart_url;
        }
      }
    } catch (err) {
      console.error(err);
    } finally {
      if (button) {
        button.removeAttribute('aria-disabled');
        button.textContent = label;
      }
    }
  });

  document.addEventListener('click', async (e) => {
    const remove = e.target.closest('[data-cart-remove]');
    if (remove) {
      e.preventDefault();
      await changeLine(remove.getAttribute('data-line'), 0);
      return;
    }

    const step = e.target.closest('[data-qty-step]');
    if (step) {
      e.preventDefault();
      const input = step.parentElement.querySelector('[data-qty-input]');
      if (!input) return;
      const delta = step.getAttribute('data-qty-step') === 'up' ? 1 : -1;
      const next = Math.max(parseInt(input.min || '0', 10), parseInt(input.value, 10) + delta);
      input.value = next;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  });

  document.addEventListener('change', async (e) => {
    const input = e.target.closest('[data-qty-input][data-line]');
    if (!input) return;
    await changeLine(input.getAttribute('data-line'), input.value);
  });

  async function changeLine(line, quantity) {
    try {
      await fetch(window.routes.cart_change_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line: Number(line), quantity: Number(quantity) })
      });
      await refreshCart();
      if (document.body.classList.contains('template-cart')) window.location.reload();
    } catch (err) {
      console.error(err);
    }
  }

  /* ---------- Product variants ---------- */

  document.querySelectorAll('[data-variant-picker]').forEach((picker) => {
    const form = document.querySelector(`[data-product-form="${picker.getAttribute('data-variant-picker')}"]`);
    const dataEl = picker.querySelector('[data-variant-json]');
    if (!form || !dataEl) return;

    const variants = JSON.parse(dataEl.textContent);
    const idInput = form.querySelector('[data-variant-id]');
    const priceEl = document.querySelector(`[data-price-for="${picker.getAttribute('data-variant-picker')}"]`);
    const button = form.querySelector('[data-add-button]');

    const update = () => {
      const chosen = [...picker.querySelectorAll('input:checked, select')].map((i) => i.value);
      const variant = variants.find((v) => v.options.every((opt, i) => opt === chosen[i]));

      if (!variant) {
        if (button) {
          button.setAttribute('aria-disabled', 'true');
          button.textContent = button.getAttribute('data-unavailable-text');
        }
        return;
      }

      if (idInput) idInput.value = variant.id;
      if (priceEl && variant.price_html) priceEl.innerHTML = variant.price_html;

      if (button) {
        if (variant.available) {
          button.removeAttribute('aria-disabled');
          button.textContent = button.getAttribute('data-add-text');
        } else {
          button.setAttribute('aria-disabled', 'true');
          button.textContent = button.getAttribute('data-soldout-text');
        }
      }

      /* Bring the image assigned to this variant into view.

         The gallery is a vertical stack on desktop and a horizontal carousel
         below the PDP breakpoint, so which axis to move is decided from the
         element rather than from a width guess: a track that overflows its own
         box horizontally is the carousel. Scrolling the window in that state
         lands on the gallery and still shows the wrong slide.

         Measured, not assumed — offsets come from bounding rects, so the maths
         holds wherever the offsetParent happens to be. */
      if (variant.media_id) {
        const sid = picker.getAttribute('data-variant-picker');
        const target = document.getElementById(`media-${sid}-${variant.media_id}`);

        if (target) {
          const gallery = target.closest('[data-gallery]');
          const isCarousel = gallery && gallery.scrollWidth > gallery.clientWidth + 1;

          /* Offset by the sticky header rather than using scrollIntoView,
             which would park the image underneath it. */
          const headerH = parseInt(
            getComputedStyle(document.documentElement).getPropertyValue('--header-h'),
            10
          ) || 53;

          if (isCarousel) {
            const delta =
              target.getBoundingClientRect().left - gallery.getBoundingClientRect().left;
            gallery.scrollTo({ left: gallery.scrollLeft + delta, behavior: 'smooth' });

            /* Moving the track is not enough once anything sits above the
               carousel. The lead image pushes it below the fold, so picking a
               colour scrolled the right slide perfectly into a place the buyer
               could not see: the swatch underlined, the picture did not move.

               Pull the page down too — but only when the track is mostly out of
               view. Someone already looking at the carousel should not have it
               yanked out from under them on every colour they try. */
            const box = gallery.getBoundingClientRect();
            const shown =
              Math.min(box.bottom, window.innerHeight) - Math.max(box.top, headerH);

            if (shown < box.height * 0.6) {
              const top = box.top + window.scrollY - headerH - 8;
              window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
            }
          } else {
            const top = target.getBoundingClientRect().top + window.scrollY - headerH - 8;
            window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
          }
        }
      }

      const url = new URL(window.location);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
    };

    picker.addEventListener('change', update);

    /* Re-clicking the colour that is already chosen fires no `change` event —
       a radio only changes when its value does. On load that is the first
       colour, so the one swatch guaranteed to be selected was the only one
       that would not bring its picture into view. `click` covers it.

       A real change fires both; update() is idempotent, and a second smooth
       scroll to a position already being scrolled to is not visible.

       Scoped to the controls themselves: the picker also holds the "Color"
       heading, and clicking a label should not move the page. */
    picker.addEventListener('click', (event) => {
      if (event.target.closest('input, label')) update();
    });
  });

  /* ---------- Media gallery sync ---------- */

  document.querySelectorAll('[data-gallery-thumb]').forEach((thumb) => {
    thumb.addEventListener('click', (e) => {
      e.preventDefault();
      const target = document.getElementById(thumb.getAttribute('data-gallery-thumb'));
      if (target) target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  });

  /* ---------- Predictive search ---------- */

  const searchInput = document.querySelector('[data-predictive-input]');
  const searchResults = document.querySelector('[data-predictive-results]');

  if (searchInput && searchResults) {
    let timer;
    searchInput.addEventListener('input', () => {
      clearTimeout(timer);
      const q = searchInput.value.trim();
      if (q.length < 2) {
        searchResults.innerHTML = '';
        return;
      }
      timer = setTimeout(async () => {
        try {
          const url = `${window.routes.predictive_search_url}?q=${encodeURIComponent(q)}&resources[type]=product,collection&resources[limit]=6&section_id=search-overlay`;
          const res = await fetch(url);
          const text = await res.text();
          const doc = new DOMParser().parseFromString(text, 'text/html');
          const fresh = doc.querySelector('[data-predictive-results]');
          if (fresh) searchResults.innerHTML = fresh.innerHTML;
        } catch (err) {
          console.error(err);
        }
      }, 200);
    });
  }

  /* ---------- Story: sticky image follows the text ----------
     The chapter that owns the middle of the viewport drives which image is
     shown. A negative rootMargin collapses the observer's band to a thin strip
     across the centre, so exactly one chapter is active at a time rather than
     every chapter that happens to be on screen. */

  document.querySelectorAll('[data-story]').forEach((story) => {
    const chapters = [...story.querySelectorAll('[data-story-chapter]')];
    const images = [...story.querySelectorAll('[data-story-image]')];
    const caption = story.querySelector('[data-story-caption]');
    if (chapters.length === 0 || images.length === 0) return;

    const captionFor = (index) => {
      const chapter = chapters[index];
      const el = chapter && chapter.querySelector('.story__caption');
      return el ? el.textContent.trim() : '';
    };

    const setActive = (index) => {
      const match = images.find((img) => img.getAttribute('data-story-image') === String(index));

      /* A chapter is allowed to have no image of its own. When one owns the
         viewport it holds whatever the frame is already showing rather than
         clearing it: a column that empties for one passage and repopulates
         reads as a fault, not as restraint. */
      if (!match) return;

      images.forEach((img) => {
        if (img === match) {
          img.setAttribute('data-active', '');
        } else {
          img.removeAttribute('data-active');
        }
      });
      if (caption) caption.textContent = captionFor(index);
    };

    /* Images are emitted in block order, so the first one is the first chapter
       that actually has an image — not necessarily chapter 0. */
    if (caption) caption.textContent = captionFor(Number(images[0].getAttribute('data-story-image')));

    if (!('IntersectionObserver' in window)) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          setActive(Number(entry.target.getAttribute('data-story-chapter')));
        });
      },
      { rootMargin: '-45% 0px -45% 0px', threshold: 0 }
    );

    chapters.forEach((chapter) => observer.observe(chapter));
  });

  /* ---------- Product gallery carousel (below the PDP breakpoint) ----------
     The scrolling is entirely CSS scroll-snap; this only reports which slide is
     showing. The index is derived from scrollLeft rather than an
     IntersectionObserver on purpose — scroll position is readable whenever the
     document is, where an observer needs a live frame loop and reports nothing
     at all in a background tab. */

  document.querySelectorAll('[data-gallery]').forEach((gallery) => {
    const readout = document.querySelector('[data-gallery-index]');
    const slides = [...gallery.querySelectorAll('.pdp__figure')];
    if (!readout || slides.length < 2) return;

    let queued = false;

    const update = () => {
      queued = false;
      const step = gallery.clientWidth;
      if (!step) return;
      const index = Math.round(gallery.scrollLeft / step) + 1;
      readout.textContent = String(Math.min(slides.length, Math.max(1, index)));

      /* A 1px tolerance: scrollLeft is fractional at some zoom levels and
         device pixel ratios, so an exact comparison never reports the end. */
      const atStart = gallery.scrollLeft <= 1;
      const atEnd = gallery.scrollLeft + gallery.clientWidth >= gallery.scrollWidth - 1;
      document.querySelectorAll('[data-gallery-step]').forEach((button) => {
        const back = Number(button.getAttribute('data-gallery-step')) < 0;
        button.setAttribute('aria-disabled', String(back ? atStart : atEnd));
      });
    };

    gallery.addEventListener(
      'scroll',
      () => {
        if (queued) return;
        queued = true;
        requestAnimationFrame(update);
      },
      { passive: true }
    );

    /* Stepping is a scroll, not a transform: the snap points stay the single
       source of truth for where a slide sits, so the buttons and a swipe can
       never disagree about which one is showing. */
    document.querySelectorAll('[data-gallery-step]').forEach((button) => {
      button.addEventListener('click', () => {
        if (button.getAttribute('aria-disabled') === 'true') return;
        const step = gallery.clientWidth * Number(button.getAttribute('data-gallery-step'));
        gallery.scrollBy({ left: step, behavior: 'smooth' });
      });
    });

    update();
  });

  /* ---------- Collection filters ---------- */

  document.addEventListener('change', (e) => {
    const form = e.target.closest('[data-filter-form]');
    if (!form) return;
    const params = new URLSearchParams(new FormData(form));
    window.location.search = params.toString();
  });

  /* ---------- Audience toggle persistence ---------- */

  document.querySelectorAll('[data-audience]').forEach((el) => {
    el.addEventListener('click', () => {
      try {
        localStorage.setItem('meridian:audience', el.getAttribute('data-audience'));
      } catch (err) {
        /* storage unavailable — non-fatal */
      }
    });
  });
})();
